import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { getActiveModule, MODULES } from "@/lib/module";
import {
  LogOut, BookOpen, LayoutDashboard, Settings as SettingsIcon, ShieldCheck,
  Hammer, LifeBuoy, Download, Users, ShieldAlert, Menu, X, FileText,
  ChevronDown, LayoutGrid, Receipt,
} from "lucide-react";

const TIER_RANK_TB = { free: 0, solo: 1, pro: 2, enterprise: 3 };

const NavLink = ({ to, icon: Icon, label, testid, onClick }) => {
  const loc = useLocation();
  const active = loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2 xl:px-2.5 pt-2 pb-1.5 text-sm font-bold uppercase tracking-wide border-b-2 transition-colors whitespace-nowrap ${
        active
          ? "text-[#00F2C3] border-[#00F2C3]"
          : "text-slate-400 border-transparent hover:text-white"
      }`}
    >
      <Icon size={18} strokeWidth={2.5} />
      {label}
    </Link>
  );
};

const DrawerLink = ({ to, icon: Icon, label, testid, onClick }) => {
  const loc = useLocation();
  const active = loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-3 px-5 py-4 border-b border-slate-100 text-base font-bold ${
        active ? "bg-[#1E1B4B] text-white" : "bg-white text-slate-900 active:bg-slate-100"
      }`}
    >
      <Icon size={20} strokeWidth={2.5} />
      {label}
    </Link>
  );
};

const UserMenuLink = ({ to, icon: Icon, label, testid, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={testid}
    className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-bold text-slate-900 hover:bg-[#00F2C3]/10 hover:text-[#00F2C3]"
  >
    <Icon size={15} strokeWidth={2.5} />
    {label}
  </Link>
);

const TopBar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Close drawer + user menu whenever the route changes
  useEffect(() => { setDrawerOpen(false); setUserMenuOpen(false); }, [location.pathname]);

  if (!user) return null;
  const isAdmin = user.role === "admin";
  const canBuild = isAdmin || (TIER_RANK_TB[user.tier] ?? 0) >= TIER_RANK_TB.pro;
  const showBilling = user.org_type !== "council";

  const closeDrawer = () => setDrawerOpen(false);

  const doLogout = () => {
    closeDrawer();
    logout();
    navigate("/login");
  };

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b-2 border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="topbar-logo">
          <img
            src="/icon-44.png"
            srcSet="/icon-44.png 1x, /icon-88.png 2x, /icon-128.png 3x"
            alt="Compliance Hub"
            width="44" height="44"
            className="h-11 w-11 object-contain"
            loading="eager" decoding="async"
          />
          <div className="leading-none hidden xl:block">
            <div className="font-black text-white text-sm tracking-[0.14em]">COMPLIANCE <span className="text-[#00F2C3]">HUB</span></div>
            <div className="text-[8px] uppercase tracking-[0.26em] text-slate-500 font-bold mt-1">All inspections. One hub.</div>
          </div>
        </Link>
        <Link to="/select-module" data-testid="topbar-module-chip"
          className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 border-2 border-[#00F2C3]/40 text-[#00F2C3] text-[11px] font-black uppercase tracking-wider hover:border-[#00F2C3] transition-colors"
          title="Switch module">
          <LayoutGrid size={12} strokeWidth={2.5}/>
          <span className="max-w-[130px] truncate">{MODULES[getActiveModule()]?.name || "Pool & Spa"}</span>
        </Link>

        {/* Desktop nav — primary tabs only; Admin/Knowledge/Team/Settings live in the user menu */}
        <nav className="hidden lg:flex items-center gap-1">
          <NavLink to="/" icon={LayoutDashboard} label="Inspections" testid="nav-dashboard" />
          <NavLink to="/statutory" icon={ShieldAlert} label={MODULES[getActiveModule()]?.tracker ? "Regulatory" : "Statutory"} testid="nav-statutory" />
          {isAdmin && <NavLink to="/admin/documents" icon={Hammer} label="Builder" testid="nav-builder" />}
          {canBuild && !isAdmin && <NavLink to="/admin/documents" icon={Hammer} label="Builder" testid="nav-builder-pro" />}
          {!isAdmin && <NavLink to="/admin/templates" icon={FileText} label="Templates" testid="nav-templates" />}
          {showBilling && <NavLink to="/documents-billing" icon={Receipt} label="Billing" testid="nav-billing" />}
          <NavLink to="/help" icon={LifeBuoy} label="Help" testid="nav-help" />
          <NavLink to="/install" icon={Download} label="Get app" testid="nav-install" />
        </nav>

        {/* Right side: user menu (desktop) OR hamburger (mobile) */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="relative hidden lg:block">
            <button
              onClick={() => setUserMenuOpen((o) => !o)}
              data-testid="user-menu-button"
              className="flex items-center gap-2.5 h-12 px-1.5 rounded-lg hover:bg-[#00F2C3]/10 transition-colors"
            >
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#00F2C3] to-[#6366F1] text-[#0B1020] font-black flex items-center justify-center text-xs shrink-0" data-testid="topbar-avatar">
                {(user.name || user.email || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div className="text-right leading-none hidden xl:block">
                <div className="text-sm font-bold text-slate-900 flex items-center justify-end gap-1.5" data-testid="topbar-user-name">
                  {user.name}
                  {isAdmin && <span title="Admin" data-testid="topbar-admin-badge" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-900 text-[9px] font-bold uppercase tracking-wider border border-teal-300"><ShieldCheck size={10} strokeWidth={3}/>Admin</span>}
                </div>
                <div className="text-xs text-slate-500">{user.email}</div>
              </div>
              <ChevronDown size={16} strokeWidth={2.5} className={`text-slate-400 transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
            </button>
            {userMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} data-testid="user-menu-backdrop" />
                <div className="absolute right-0 top-full mt-1 w-60 bg-white border-2 border-slate-200 shadow-2xl z-50 py-1" data-testid="user-menu-panel">
                  <div className="px-4 py-2.5 border-b border-slate-100 xl:hidden">
                    <div className="text-sm font-bold text-slate-900 truncate">{user.name}</div>
                    <div className="text-xs text-slate-500 truncate">{user.email}</div>
                  </div>
                  {user.is_platform_owner && <UserMenuLink to="/admin/platform" icon={ShieldCheck} label="Platform Administration" testid="menu-platform-admin" onClick={() => setUserMenuOpen(false)} />}
                  <UserMenuLink to="/knowledge" icon={BookOpen} label="Knowledge" testid="menu-knowledge" onClick={() => setUserMenuOpen(false)} />
                  <UserMenuLink to="/team" icon={Users} label="Team" testid="menu-team" onClick={() => setUserMenuOpen(false)} />
                  <UserMenuLink to="/settings" icon={SettingsIcon} label="Settings" testid="menu-settings" onClick={() => setUserMenuOpen(false)} />
                  <UserMenuLink to="/select-module" icon={LayoutGrid} label="Switch module" testid="menu-switch-module" onClick={() => setUserMenuOpen(false)} />
                  <div className="my-1 border-t border-slate-100" />
                  <button
                    onClick={doLogout}
                    data-testid="logout-button"
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-bold text-red-700 hover:bg-red-50 text-left"
                  >
                    <LogOut size={15} strokeWidth={2.5} /> Sign out
                  </button>
                </div>
              </>
            )}
          </div>
          {/* Mobile hamburger */}
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="mobile-menu-open"
            aria-label="Open menu"
            className="lg:hidden h-11 w-11 inline-flex items-center justify-center border-2 border-slate-900 bg-[#1E1B4B] text-white"
          >
            <Menu size={20} strokeWidth={2.5}/>
          </button>
        </div>
      </div>

      {/* Mobile scrolling pill row — primary tabs only, everything else lives in the drawer */}
      <nav className="lg:hidden border-t border-slate-200 px-2 py-2 flex justify-around gap-1 overflow-x-auto" data-testid="mobile-pill-nav">
        <NavLink to="/" icon={LayoutDashboard} label="Inspections" testid="nav-dashboard-mobile" />
        <NavLink to="/statutory" icon={ShieldAlert} label={MODULES[getActiveModule()]?.tracker ? "Regulatory" : "Statutory"} testid="nav-statutory-mobile" />
        {canBuild && <NavLink to="/admin/documents" icon={Hammer} label="Builder" testid="nav-builder-mobile" />}
        {!isAdmin && <NavLink to="/admin/templates" icon={FileText} label="Templates" testid="nav-templates-mobile" />}
        {showBilling && <NavLink to="/documents-billing" icon={Receipt} label="Billing" testid="nav-billing-mobile" />}
        <NavLink to="/help" icon={LifeBuoy} label="Help" testid="nav-help-mobile" />
      </nav>

      {/* Full-height mobile drawer with EVERY nav item (portal escapes the blurred header) */}
      {drawerOpen && createPortal(
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-slate-900/70" onClick={closeDrawer}/>
          <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-white flex flex-col shadow-2xl">
            <div className="h-16 px-4 flex items-center justify-between border-b-2 border-slate-200 shrink-0">
              <div className="min-w-0">
                <div className="font-black text-slate-900 text-sm flex items-center gap-2 truncate">
                  {user.name}
                  {isAdmin && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-teal-100 text-teal-900 text-[9px] font-bold uppercase tracking-wider border border-teal-300"><ShieldCheck size={10} strokeWidth={3}/>Admin</span>}
                </div>
                <div className="text-[11px] text-slate-500 truncate">{user.email}</div>
              </div>
              <button onClick={closeDrawer} data-testid="mobile-drawer-close" className="h-10 w-10 inline-flex items-center justify-center border-2 border-slate-300"><X size={18}/></button>
            </div>
            <nav className="flex-1 overflow-y-auto">
              <div className="px-5 pt-4 pb-2 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400" data-testid="drawer-section-account">Account</div>
              {user.is_platform_owner && <DrawerLink to="/admin/platform" icon={ShieldCheck} label="Platform Administration" testid="drawer-nav-platform" onClick={closeDrawer}/>}
              {showBilling && <DrawerLink to="/documents-billing" icon={Receipt} label="Documents & Billing" testid="drawer-nav-billing" onClick={closeDrawer}/>}
              <DrawerLink to="/knowledge" icon={BookOpen} label="Knowledge Base" testid="drawer-nav-knowledge" onClick={closeDrawer}/>
              <DrawerLink to="/team" icon={Users} label="Team" testid="drawer-nav-team" onClick={closeDrawer}/>
              <DrawerLink to="/settings" icon={SettingsIcon} label="Settings" testid="drawer-nav-settings" onClick={closeDrawer}/>
              <DrawerLink to="/install" icon={Download} label="Get the app" testid="drawer-nav-install" onClick={closeDrawer}/>
              <DrawerLink to="/select-module" icon={LayoutGrid} label="Switch module" testid="drawer-switch-module" onClick={closeDrawer}/>
            </nav>
            <div className="p-4 border-t-2 border-slate-200 shrink-0">
              <button
                data-testid="drawer-logout"
                onClick={doLogout}
                className="w-full h-12 inline-flex items-center justify-center gap-2 bg-white border-2 border-slate-900 text-slate-900 font-bold text-sm hover:bg-slate-100"
              >
                <LogOut size={16} strokeWidth={2.5}/> Sign out
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
};

export default TopBar;
